/**
 * PatternEditor — standaard corvee-beschikbaarheid per persoon, met aparte
 * even/oneven-week varianten. Niet-werkdagen (uit Airtable "Standaard Werkdagen")
 * staan vast op Vrij; werkdagen toggle je Aanwezig (kantoor) ↔ Thuis.
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import { DAYS, STATUS, fullWeek, isWorkday, defaultPatternFromWorkdays } from "./corveeLogic";

const T = {
  primary: "#526075", text: "#2d3335", secondary: "#556071", outline: "#767c7e",
  surfaceLow: "#f1f4f5", font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};
const Icon = ({ name, size = 15, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, ...sx }}>{name}</span>
);

function Row({ label, pattern, workdays, onCell, readOnly }) {
  const row = (pattern && pattern.length === 5) ? pattern : fullWeek();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.72rem", fontWeight: 700, color: T.outline, width: 92 }}>{label}</Typography>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {DAYS.map((d, i) => {
          const work = isWorkday(workdays, i);
          const key = work ? (row[i] === "home" ? "home" : "office") : "free";
          const st = STATUS[key];
          const clickable = work && !readOnly;
          return (
            <Box key={d} onClick={clickable ? () => onCell(i) : undefined} title={`${d}: ${st.label}${work ? "" : " (standaard vrij)"}`} sx={{ minWidth: 52, borderRadius: "0.5rem", py: 0.6, px: 0.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.2, cursor: clickable ? "pointer" : "default", bgcolor: st.bg, color: st.color, opacity: work ? 1 : 0.7, "&:hover": clickable ? { opacity: 0.85 } : {} }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.56rem", fontWeight: 700, color: T.outline }}>{d.slice(0, 2)}</Typography>
              <Icon name={st.icon} sx={{ color: st.color }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.56rem", fontWeight: 700 }}>{st.short}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function PatternEditor({ value, workdays, onChange, readOnly = false }) {
  const base = defaultPatternFromWorkdays(workdays);
  // Niet-werkdagen forceren op 'free'; werkdagen nemen opgeslagen waarde (default office).
  const merge = (p) => base.map((b, i) => (b === "free" ? "free" : (p?.[i] === "home" ? "home" : "office")));
  const even = merge(value?.pattern_even?.length === 5 ? value.pattern_even : base);
  const odd = merge(value?.pattern_odd?.length === 5 ? value.pattern_odd : base);
  const toggle = (which, i) => {
    if (!isWorkday(workdays, i)) return;
    const arr = (which === "even" ? even : odd).slice();
    arr[i] = arr[i] === "office" ? "home" : "office";
    onChange({ pattern_even: which === "even" ? arr : even, pattern_odd: which === "odd" ? arr : odd });
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Row label="Even week" pattern={even} workdays={workdays} onCell={(i) => toggle("even", i)} readOnly={readOnly} />
      <Row label="Oneven week" pattern={odd} workdays={workdays} onCell={(i) => toggle("odd", i)} readOnly={readOnly} />
      {!readOnly && <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.68rem", color: T.outline }}>Vrije dagen komen uit je standaard werkdagen. Klik een werkdag om te wisselen tussen Aanwezig (kantoor) en Thuis. Dit is de terugval wanneer je een corveeweek niet zelf invult.</Typography>}
    </Box>
  );
}
